import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
  cn,
  Popover,
} from '@c2fo/liquidity';
import { ArrowRightFromLineDuotoneIcon, ClockRotateLeftDuotoneIcon, ShapesDuotoneIcon } from '@c2fo/liquidity/icons';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import ClosedMarketChip from '@/enterprise/components/ClosedMarketChip';
import Skeleton from '@/enterprise/components/Skeleton';
import useClosedMarkets from '@/enterprise/data/useClosedMarkets';
import useGroupByBuyer, { sortTakerMarkets } from '@/enterprise/data/useGroupByBuyer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { GlobalParticipationWithContext } from '@/enterprise/features/globalOffers/GlobalParticipationContext';
import { OfferModalWithContext } from '@/enterprise/features/offerModal/OfferModalContext';
import NoAvailableMarkets from '@/enterprise/features/takerMarketTabs/NoAvailableMarkets';
import { OfferBuilderNavigationState } from '@/enterprise/pages/OfferBuilder';
import { useReporting } from '@/reporting';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import { IntercomDataTargetNames } from '@/reporting/intercomDataTargetNames';
import { ButtonLink } from '@/shared/components/ButtonLink';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import { isPreferredOfferNearTermEnd } from '@/utils/preferredOfferRenewal';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useRestrictions from '@/utils/useRestrictions';
import BuyerLogo from '../BuyerLogo';
import ErrorLoadingMarkets from '../ErrorLoadingMarkets';
import NewDivisionAlert, {
  getNewDivisionAlertEligibleTakerMarkets,
  useEnableNewDivisionAlert,
} from '../NewDivisionAlert';
import CustomerStats from './CustomerStats';
import DivisionTable from './DivisionTable';
import { EditRateModalWithContext } from './EditRateModal/EditRateModalContext';
import { EditScheduleModalWithContext } from './EditScheduleModal/EditScheduleModalContext';
import OfferActivityActionsDropdown from './OfferActivityActionsDropdown';
import { useOfferActivityFilters } from './OfferActivityFilterContext';
import OfferDetails from './OfferDetails';

export const getOfferBuilderNavigationState = (
  takerMarkets: TakerMarket[],
  { expandSelected = false }: { expandSelected?: boolean } = {}
) =>
  ({
    frequency: null,
    preselectedTakerMarketIds: takerMarkets.map((tm) => tm.id),
    prioritizeSelected: true,
    expandSelected,
  }) satisfies OfferBuilderNavigationState;

const createHandleBuildOfferClick =
  (
    trackEnterpriseEvent: ReturnType<typeof useReporting>['trackEnterpriseEvent'],
    buyer: {
      makerOrganizationUuid: string;
      makerOrganizationName: string;
      stats: { totalInvoiceAmount: number };
    }
  ) =>
  () => {
    trackEnterpriseEvent('build-offer-cta::clicked', {
      source: 'offer-activity-card',
      customerUuid: buyer.makerOrganizationUuid,
      customerName: buyer.makerOrganizationName,
      totalAmount: buyer.stats.totalInvoiceAmount,
    });
  };

const OfferActivityCardsComponent = () => {
  const enableNewDivisionAlert = useEnableNewDivisionAlert();
  const getNewDivisionAlertEligible = enableNewDivisionAlert ? getNewDivisionAlertEligibleTakerMarkets : undefined;

  const { t } = useTranslation();
  const { asCurrency } = useLocaleFormat();
  const { getRestrictions } = useRestrictions();
  const { selectedCurrency } = useCurrencyContext();
  const { trackEnterpriseEvent } = useReporting();
  const { getClosedMarkets } = useClosedMarkets();
  // taker markets are used for offer activity cards, filtered taker markets are used for table data
  // this allows for the offer activity cards to remain independent of the table filters and show the same
  // rollup data while the table data is filtered to the taker markets that match the filters
  const { filteredTakerMarkets, takerMarkets } = useOfferActivityFilters();
  const buyers = useGroupByBuyer({ takerMarkets, prioritizeNewDivisions: enableNewDivisionAlert });
  const [expandedMakerOrganizationUuids, setExpandedMakerOrganizationUuids] = useState<string[]>([]);

  if (buyers.length === 0) {
    return <NoAvailableMarkets />;
  }

  return (
    <Accordion
      className="space-y-4"
      type="multiple"
      value={expandedMakerOrganizationUuids}
      onValueChange={setExpandedMakerOrganizationUuids}
    >
      {buyers.map((buyer, index) => {
        const { canEditOffers } = getRestrictions(buyer.takerMarkets);
        // filter buyer markets only for table
        const filteredBuyerMarkets = filteredTakerMarkets.filter(
          (market) => market.makerOrganizationUuid === buyer.makerOrganizationUuid
        );

        // do not show buyer if no markets match filters
        if (filteredBuyerMarkets.length === 0) {
          return null;
        }

        const navigationState = getOfferBuilderNavigationState(buyer.takerMarkets);
        const takerMarketGroupValues = Object.values(buyer.takerMarketGroups);
        let hasInvoiceRules = false;
        let hasManuallyExcludedInvoices = false;
        const hasPreferredOffersNearTermEnd = buyer.takerMarkets.some((tm) => isPreferredOfferNearTermEnd(tm));

        for (const group of takerMarketGroupValues) {
          if (group.takerMarketsWithRules.length > 0) {
            hasInvoiceRules = true;
          }

          if (group.rollup.excludedAmount > 0) {
            hasManuallyExcludedInvoices = true;
          }

          if (hasInvoiceRules && hasManuallyExcludedInvoices) {
            break;
          }
        }

        const hasActiveOffers = buyer.takerMarkets.some(
          (takerMarket) => takerMarket.offerConfig.isEnabled || takerMarket.offerSchedule
        );

        const handleBuildOfferClick = createHandleBuildOfferClick(trackEnterpriseEvent, buyer);

        const { hasClosedMarkets, closedMarketCount, closedInvoiceAmount } = getClosedMarkets(
          buyer.takerMarkets,
          'total'
        );
        const formattedClosedInvoiceAmount = asCurrency(closedInvoiceAmount, selectedCurrency);

        return (
          <div key={buyer.makerOrganizationUuid}>
            <AccordionItem
              key={buyer.makerOrganizationUuid}
              className="overflow-hidden rounded-2xl bg-white shadow"
              value={buyer.makerOrganizationUuid}
              {...(index === 0 &&
                expandedMakerOrganizationUuids.includes(buyer.makerOrganizationUuid) && {
                  'data-intercom-target': 'customerCardExpanded' satisfies IntercomDataTargetNames,
                })}
            >
              <AccordionHeader className="flex-col p-4">
                <div className="flex w-full flex-col items-center gap-y-4 @container/activity-card md:flex-row">
                  <div
                    className={cn('flex w-full flex-col items-center justify-between gap-4', {
                      '@[768px]/activity-card:flex-row': hasActiveOffers,
                      'md:flex-row': !hasActiveOffers,
                    })}
                  >
                    {/* left, logo/buyer info */}
                    <div className="flex w-full items-center gap-x-4 truncate">
                      <BuyerLogo
                        buyerLogoUrl={buyer.makerOrganizationLogo}
                        buyerName={buyer.makerOrganizationName}
                        newDivisionCount={buyer.stats.newDivisionCount}
                        referrer="offer-activity"
                        size="xl"
                      />
                      <div className="truncate">
                        <div className="truncate text-xl">{buyer.makerOrganizationName}</div>
                        <div className="truncate text-xl font-bold">
                          {asCurrency(buyer.stats.totalInvoiceAmount, selectedCurrency)}
                        </div>
                        <div className="text-sm text-text-secondary">{t('core.totalAmount')}</div>
                        {hasInvoiceRules ||
                        hasManuallyExcludedInvoices ||
                        hasClosedMarkets ||
                        hasPreferredOffersNearTermEnd ? (
                          <div className="mt-1 flex gap-1">
                            {hasManuallyExcludedInvoices ? (
                              <Popover>
                                <Popover.Trigger aria-label={t('core.manualInvoiceExclusions')}>
                                  <ArrowRightFromLineDuotoneIcon className="size-4 fill-blue-700" />
                                </Popover.Trigger>
                                <Popover.Content arrow collisionPadding={24}>
                                  <div className="w-40 text-center">{t('core.someInvoicesManuallyExcluded')}</div>
                                </Popover.Content>
                              </Popover>
                            ) : null}
                            {hasInvoiceRules ? (
                              <Popover>
                                <Popover.Trigger aria-label={t('automation.invoiceRules')}>
                                  <ShapesDuotoneIcon className="size-4 fill-blue-700" />
                                </Popover.Trigger>
                                <Popover.Content arrow collisionPadding={24}>
                                  <div className="w-44 text-center">{t('core.someInvoicesManagedByInvoiceRules')}</div>
                                </Popover.Content>
                              </Popover>
                            ) : null}
                            {hasPreferredOffersNearTermEnd ? (
                              <Popover>
                                <Popover.Trigger aria-label={t('core.preferredOfferNearTermEnd')}>
                                  <ClockRotateLeftDuotoneIcon className="size-4 fill-red-700" />
                                </Popover.Trigger>
                                <Popover.Content arrow collisionPadding={24}>
                                  <div className="w-44 text-center">
                                    {t('preferredRenewal.oneOrMoreOffersExpiringSoon')}
                                  </div>
                                </Popover.Content>
                              </Popover>
                            ) : null}
                            {hasClosedMarkets ? (
                              <ClosedMarketChip
                                count={closedMarketCount}
                                invoiceAmount={formattedClosedInvoiceAmount}
                              />
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                      {/* mobile actions dropdown */}
                      {hasActiveOffers ? (
                        <div className="ml-auto flex md:hidden">
                          <OfferActivityActionsDropdown buyer={buyer} />
                        </div>
                      ) : null}
                    </div>
                    {/* right, offer details and actions */}
                    <div className="flex w-full items-center justify-end gap-x-2">
                      {hasActiveOffers ? <OfferDetails buyer={buyer} /> : null}
                      {/* desktop trigger */}
                      <div className="hidden md:flex md:shrink-0 md:gap-x-2">
                        {hasActiveOffers ? (
                          <OfferActivityActionsDropdown buyer={buyer} />
                        ) : (
                          <ButtonLink
                            disabled={!canEditOffers}
                            size="sm"
                            state={navigationState}
                            to={asEnterpriseLink('build-offer')}
                            onClick={handleBuildOfferClick}
                          >
                            {t('core.buildOffer')}
                          </ButtonLink>
                        )}
                        {index === 0 ? (
                          <IntercomDataTarget target="customerCardExpandArrow">
                            <AccordionTrigger className="ml-0" />
                          </IntercomDataTarget>
                        ) : (
                          <AccordionTrigger className="ml-0" />
                        )}
                      </div>
                    </div>
                  </div>
                  {/* mobile build offer/trigger */}
                  <div className="w-full space-y-2 md:hidden">
                    {!hasActiveOffers ? (
                      <ButtonLink
                        disabled={!canEditOffers}
                        full
                        state={navigationState}
                        to={asEnterpriseLink('build-offer')}
                        onClick={handleBuildOfferClick}
                      >
                        {t('core.buildOffer')}
                      </ButtonLink>
                    ) : null}
                    <AccordionTrigger className="ml-0 h-11 w-full" variant="tertiary" />
                  </div>
                </div>
                <div className="w-full">
                  <NewDivisionAlert takerMarkets={buyer.takerMarkets} referrer="offer-activity" className="pt-4" />
                </div>
              </AccordionHeader>
              <AccordionContent className="overflow-auto p-0">
                <QueryBoundaries
                  LoadingComponent={() => (
                    <div className="space-y-4 px-4 pb-4">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <Skeleton key={index} height="h-24" />
                      ))}
                    </div>
                  )}
                >
                  <CustomerStats buyer={buyer} />
                  <DivisionTable
                    takerMarkets={sortTakerMarkets(
                      filteredBuyerMarkets,
                      {
                        key: 'totalInvoiceAmount',
                        direction: 'desc',
                      },
                      getNewDivisionAlertEligible
                    )}
                  />
                </QueryBoundaries>
              </AccordionContent>
            </AccordionItem>
          </div>
        );
      })}
    </Accordion>
  );
};

const OfferActivityCards = () => (
  <QueryBoundaries
    LoadingComponent={() => (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, skeletonIndex) => (
          <Skeleton key={skeletonIndex} height="h-44" />
        ))}
      </div>
    )}
    ErrorComponent={() => <ErrorLoadingMarkets />}
  >
    <GlobalParticipationWithContext>
      <OfferModalWithContext>
        <EditRateModalWithContext>
          <EditScheduleModalWithContext>
            <OfferActivityCardsComponent />
          </EditScheduleModalWithContext>
        </EditRateModalWithContext>
      </OfferModalWithContext>
    </GlobalParticipationWithContext>
  </QueryBoundaries>
);

export default OfferActivityCards;
